/**
 * Вход устройства: клиент держит `userId` и ключ устройства, а сервер по
 * ним выдаёт короткоживущий токен доступа (`Authorization: Bearer`).
 *
 * Ключ устройства — долгий секрет (как refresh-токен), токен — подписанный
 * JWT на месяц. Аккаунт открывается только входом по почте (код из письма)
 * с принятыми Пользовательским соглашением и Политикой конфиденциальности;
 * ключ устройства лишь продлевает его сессию.
 */

import { z } from 'zod';

import { UiLanguageCodeSchema } from './language';

/** Ключ устройства: 32 случайных байта в base64url (43 символа). */
export const DEVICE_KEY_PATTERN = /^[A-Za-z0-9_-]{43}$/;

export const OpenSessionSchema = z.object({
  /** Чей это ключ: сервер сверяет, но верит только ключу. */
  userId: z.string().uuid().optional(),
  /** Единственное доказательство владения аккаунтом. */
  deviceKey: z.string().regex(DEVICE_KEY_PATTERN).optional(),
});
export type OpenSession = z.infer<typeof OpenSessionSchema>;

export interface AuthSession {
  readonly userId: string;
  readonly deviceKey: string;
  /** Почта аккаунта; `null` — анонимный аккаунт на устройстве. */
  readonly email: string | null;
  readonly token: string;
  /** ISO-время, когда токен перестанет приниматься. */
  readonly expiresAt: string;
}

/** Почта для входа: без пробелов и в нижнем регистре — один адрес, один аккаунт. */
export const EmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .email();

export const RequestCodeSchema = z.object({
  email: EmailSchema,
  /** Язык письма — язык интерфейса ученика. */
  language: UiLanguageCodeSchema.optional(),
});
export type RequestCode = z.infer<typeof RequestCodeSchema>;

/** Код входа: 6 цифр из письма. */
export const LOGIN_CODE_PATTERN = /^\d{6}$/;

/**
 * Согласия, данные галочками на экране входа. Соглашение и Политика —
 * обязательно (`true`): без них аккаунт не открывается; рассылки — по желанию.
 */
export const ConsentsSchema = z.object({
  terms: z.literal(true, { errorMap: () => ({ message: 'Нужно принять Пользовательское соглашение и Политику конфиденциальности' }) }),
  newsletter: z.boolean().default(false),
});
export type Consents = z.input<typeof ConsentsSchema>;

export const VerifyCodeSchema = z.object({
  email: EmailSchema,
  code: z.string().trim().regex(LOGIN_CODE_PATTERN),
  consents: ConsentsSchema,
  /**
   * Анонимный аккаунт этого устройства (с ключом — доказательство, что он
   * свой): его прогресс сливается в аккаунт с почтой.
   */
  anonymous: z.object({ userId: z.string().uuid(), deviceKey: z.string().regex(DEVICE_KEY_PATTERN) }).optional(),
});
export type VerifyCode = z.infer<typeof VerifyCodeSchema>;

export const LogoutSchema = z.object({ userId: z.string().uuid(), deviceKey: z.string().regex(DEVICE_KEY_PATTERN) });
export type Logout = z.infer<typeof LogoutSchema>;
